"use client";

import * as React from "react";
import {
  Text,
  SectionCard,
  CardTitle,
  CardDescription,
  Section,
  SearchBox,
  Badge,
} from "@/src/components";
import { Filter, Sparkles } from "lucide-react";
import { InstallCommand } from "@/components/docs/install-command";

export default function DocsComponentsSearchBox() {
  // Basic controlled example
  const [basicValue, setBasicValue] = React.useState("");

  // Search submit example
  const [searchValue, setSearchValue] = React.useState("");
  const [searchResult, setSearchResult] = React.useState<string | null>(null);

  // Filter example
  const [filterValue, setFilterValue] = React.useState("");
  const items = ["Apple", "Banana", "Cherry", "Date", "Elderberry", "Fig", "Grape"];
  const filteredItems = items.filter((item) =>
    item.toLowerCase().includes(filterValue.toLowerCase())
  );

  return (
    <>
      {/* Header */}
      <Section>
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Text as="h1" className="text-h2 font-bold">
              Search Box
            </Text>
            <Badge variant="secondary">Input</Badge>
          </div>
          <Text className="text-lg text-muted-foreground max-w-2xl">
            A search input with icon, clear button, and submit handling. Supports
            controlled and uncontrolled modes with customizable icons.
          </Text>
        </div>
      </Section>

      {/* Installation */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Installation</CardTitle>
              <CardDescription>Add this component to your project</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <InstallCommand command="npx shadcn@latest add @bluecopa-ui/search-box" />
        </SectionCard>
      </Section>

      {/* Usage */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Usage</CardTitle>
              <CardDescription>Import and use in your application</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm overflow-x-auto">
            <pre>{`import { SearchBox } from "@/components/input/search-box";

function MyComponent() {
  const [search, setSearch] = React.useState("");

  return (
    <SearchBox
      value={search}
      onChange={setSearch}
      placeholder="Search..."
    />
  );
}`}</pre>
          </div>
        </SectionCard>
      </Section>

      {/* Interactive Demo */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Interactive Demo</CardTitle>
              <CardDescription>Try the search box with live state</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-6"
        >
          <div className="max-w-md">
            <SearchBox
              value={basicValue}
              onChange={setBasicValue}
              placeholder="Type to search..."
            />
          </div>

          <div className="flex items-center gap-2 text-sm">
            <Text className="text-muted-foreground">Current value:</Text>
            <code className="bg-muted px-2 py-1 rounded text-xs">
              {basicValue || "(empty)"}
            </code>
          </div>
        </SectionCard>
      </Section>

      {/* With Search Submit */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Search Submit</CardTitle>
              <CardDescription>Trigger search on Enter key press</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-6"
        >
          <div className="max-w-md">
            <SearchBox
              value={searchValue}
              onChange={setSearchValue}
              onSearch={(value) => setSearchResult(`Searched for: "${value}"`)}
              placeholder="Press Enter to search..."
            />
          </div>

          {searchResult && (
            <div className="p-3 bg-accent/50 rounded-lg text-sm">
              {searchResult}
            </div>
          )}

          <div className="bg-muted/50 rounded-lg p-4 font-mono text-xs overflow-x-auto">
            <pre>{`<SearchBox
  value={search}
  onChange={setSearch}
  onSearch={(value) => handleSearch(value)}
  placeholder="Press Enter to search..."
/>`}</pre>
          </div>
        </SectionCard>
      </Section>

      {/* Live Filtering */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Live Filtering</CardTitle>
              <CardDescription>Filter a list as you type</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <div className="max-w-md">
            <SearchBox
              value={filterValue}
              onChange={setFilterValue}
              placeholder="Filter fruits..."
            />
          </div>

          <div className="flex flex-wrap gap-2">
            {filteredItems.length > 0 ? (
              filteredItems.map((item) => (
                <Badge key={item} variant="secondary">
                  {item}
                </Badge>
              ))
            ) : (
              <Text className="text-sm text-muted-foreground">No results found</Text>
            )}
          </div>
        </SectionCard>
      </Section>

      {/* Custom Icons */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Custom Icons</CardTitle>
              <CardDescription>Replace default search and clear icons</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-6"
        >
          <div className="grid gap-4 max-w-md">
            <div className="space-y-2">
              <Text className="text-sm font-medium">Default</Text>
              <SearchBox placeholder="Default icons..." />
            </div>

            <div className="space-y-2">
              <Text className="text-sm font-medium">Custom search icon</Text>
              <SearchBox
                searchIcon={<Filter className="h-4 w-4" />}
                placeholder="Filter items..."
              />
            </div>

            <div className="space-y-2">
              <Text className="text-sm font-medium">AI-powered search</Text>
              <SearchBox
                searchIcon={<Sparkles className="h-4 w-4" />}
                placeholder="Ask anything..."
              />
            </div>
          </div>

          <div className="bg-muted/50 rounded-lg p-4 font-mono text-xs overflow-x-auto">
            <pre>{`<SearchBox
  searchIcon={<Sparkles className="h-4 w-4" />}
  placeholder="Ask anything..."
/>`}</pre>
          </div>
        </SectionCard>
      </Section>

      {/* Without Clear Button */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Without Clear Button</CardTitle>
              <CardDescription>Hide the clear button</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <div className="max-w-md">
            <SearchBox
              defaultValue="Cannot clear this"
              showClear={false}
              placeholder="No clear button..."
            />
          </div>

          <div className="bg-muted/50 rounded-lg p-4 font-mono text-xs">
            <code>{`<SearchBox showClear={false} />`}</code>
          </div>
        </SectionCard>
      </Section>

      {/* Uncontrolled Mode */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Uncontrolled Mode</CardTitle>
              <CardDescription>Use defaultValue for uncontrolled state</CardDescription>
            </>
          }
          contentClassName="p-6 space-y-4"
        >
          <div className="max-w-md">
            <SearchBox
              defaultValue="Initial value"
              onSearch={(value) => alert(`Searched: ${value}`)}
              placeholder="Uncontrolled search..."
            />
          </div>

          <div className="bg-muted/50 rounded-lg p-4 font-mono text-xs overflow-x-auto">
            <pre>{`<SearchBox
  defaultValue="Initial value"
  onSearch={(value) => handleSearch(value)}
/>`}</pre>
          </div>
        </SectionCard>
      </Section>

      {/* Props Table */}
      <Section>
        <SectionCard
          header={<CardTitle>Props</CardTitle>}
          contentClassName="p-6"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 pr-4 font-semibold">Prop</th>
                  <th className="text-left py-2 pr-4 font-semibold">Type</th>
                  <th className="text-left py-2 pr-4 font-semibold">Default</th>
                  <th className="text-left py-2 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                <tr>
                  <td className="py-2 pr-4 font-mono text-xs">value</td>
                  <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">string</td>
                  <td className="py-2 pr-4 text-muted-foreground">-</td>
                  <td className="py-2 text-muted-foreground">Controlled search value</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-mono text-xs">defaultValue</td>
                  <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">string</td>
                  <td className="py-2 pr-4 font-mono text-xs">""</td>
                  <td className="py-2 text-muted-foreground">Initial value for uncontrolled mode</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-mono text-xs">onChange</td>
                  <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">(value: string) =&gt; void</td>
                  <td className="py-2 pr-4 text-muted-foreground">-</td>
                  <td className="py-2 text-muted-foreground">Called when value changes</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-mono text-xs">onSearch</td>
                  <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">(value: string) =&gt; void</td>
                  <td className="py-2 pr-4 text-muted-foreground">-</td>
                  <td className="py-2 text-muted-foreground">Called on Enter key press</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-mono text-xs">onClear</td>
                  <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">() =&gt; void</td>
                  <td className="py-2 pr-4 text-muted-foreground">-</td>
                  <td className="py-2 text-muted-foreground">Called when clear button is clicked</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-mono text-xs">showClear</td>
                  <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">boolean</td>
                  <td className="py-2 pr-4 font-mono text-xs">true</td>
                  <td className="py-2 text-muted-foreground">Show clear button when value exists</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-mono text-xs">searchIcon</td>
                  <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">ReactNode</td>
                  <td className="py-2 pr-4 font-mono text-xs">&lt;Search /&gt;</td>
                  <td className="py-2 text-muted-foreground">Custom search icon</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-mono text-xs">clearIcon</td>
                  <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">ReactNode</td>
                  <td className="py-2 pr-4 font-mono text-xs">&lt;X /&gt;</td>
                  <td className="py-2 text-muted-foreground">Custom clear icon</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-mono text-xs">placeholder</td>
                  <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">string</td>
                  <td className="py-2 pr-4 font-mono text-xs">"Search..."</td>
                  <td className="py-2 text-muted-foreground">Placeholder text</td>
                </tr>
                <tr>
                  <td className="py-2 pr-4 font-mono text-xs">containerClassName</td>
                  <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">string</td>
                  <td className="py-2 pr-4 text-muted-foreground">-</td>
                  <td className="py-2 text-muted-foreground">Class for outer container</td>
                </tr>
              </tbody>
            </table>
          </div>
        </SectionCard>
      </Section>

      {/* Accessibility */}
      <Section>
        <SectionCard
          header={<CardTitle>Accessibility</CardTitle>}
          contentClassName="p-6 space-y-4"
        >
          <ul className="space-y-2 text-sm">
            <li>• <strong>Keyboard:</strong> Focus with Tab, submit with Enter, clear with Escape (when focused)</li>
            <li>• <strong>Clear button:</strong> Has <code className="bg-muted px-1 rounded">aria-label="Clear search"</code> for screen readers</li>
            <li>• <strong>Input type:</strong> Uses <code className="bg-muted px-1 rounded">type="search"</code> for native browser behavior</li>
            <li>• <strong>Focus management:</strong> Maintains focus after clearing</li>
          </ul>

          <div className="bg-muted/50 rounded-lg p-4 font-mono text-xs overflow-x-auto">
            <pre>{`{/* Add aria-label for context */}
<SearchBox
  aria-label="Search products"
  placeholder="Search products..."
/>`}</pre>
          </div>
        </SectionCard>
      </Section>

      {/* Guidelines */}
      <Section>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <SectionCard
            header={<CardTitle className="text-success">✓ Do</CardTitle>}
            contentClassName="p-6"
          >
            <ul className="space-y-2 text-sm">
              <li>• Use clear, action-oriented placeholder text</li>
              <li>• Show search results as user types (live filtering)</li>
              <li>• Preserve search value across navigation</li>
              <li>• Add aria-label when context isn't clear</li>
              <li>• Use onSearch for server-side searches</li>
            </ul>
          </SectionCard>

          <SectionCard
            header={<CardTitle className="text-destructive">✗ Don't</CardTitle>}
            contentClassName="p-6"
          >
            <ul className="space-y-2 text-sm">
              <li>• Don't hide the clear button for long inputs</li>
              <li>• Don't use generic placeholders like "Search"</li>
              <li>• Don't require exact matches for filtering</li>
              <li>• Don't auto-submit on every keystroke (debounce)</li>
              <li>• Don't reset search on component re-render</li>
            </ul>
          </SectionCard>
        </div>
      </Section>

      {/* Related Components */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Related Components</CardTitle>
              <CardDescription>Components often used with SearchBox</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <a href="/docs/components/input" className="p-4 border rounded-lg hover:bg-accent/50 transition-colors">
              <Text className="font-medium">Input</Text>
              <Text className="text-sm text-muted-foreground">Basic text input</Text>
            </a>
            <a href="/docs/components/combobox" className="p-4 border rounded-lg hover:bg-accent/50 transition-colors">
              <Text className="font-medium">Combobox</Text>
              <Text className="text-sm text-muted-foreground">Search with autocomplete</Text>
            </a>
            <a href="/docs/components/command" className="p-4 border rounded-lg hover:bg-accent/50 transition-colors">
              <Text className="font-medium">Command</Text>
              <Text className="text-sm text-muted-foreground">Command palette search</Text>
            </a>
          </div>
        </SectionCard>
      </Section>
    </>
  );
}
